import { useEffect, useMemo, useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LaunchIcon from '@mui/icons-material/Launch';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {
    Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel,
    MenuItem, Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';

import {
    createPaymentLink, deactivatePaymentLink, getPaymentCountries,
    getPaymentEvidence, getPaymentLinks, getSubscriptionPlans,
    lookupSubscriptionUser, type AdminSubscriptionPlan,
    type PaymentCountryOption, type PaymentEvidenceRecord,
    type PaymentLinkRecord, type SubscriptionUserLookup,
} from './paymentOverviewApi';

function formatDateTime(value: string) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

export function PaymentLinksPanel() {
    const [identifier, setIdentifier] = useState('');
    const [user, setUser] = useState<SubscriptionUserLookup | null>(null);
    const [plans, setPlans] = useState<AdminSubscriptionPlan[]>([]);
    const [countries, setCountries] = useState<PaymentCountryOption[]>([]);
    const [planId, setPlanId] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [expiresInHours, setExpiresInHours] = useState(24);
    const [links, setLinks] = useState<PaymentLinkRecord[]>([]);
    const [evidence, setEvidence] = useState<PaymentEvidenceRecord[]>([]);
    const [created, setCreated] = useState<PaymentLinkRecord | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        Promise.all([getSubscriptionPlans(), getPaymentCountries()])
            .then(([planRows, countryRows]) => {
                setPlans(planRows.filter((plan) => plan.isActive));
                setCountries(countryRows.filter((country) => country.isAirwallexSupported));
            })
            .catch((reason) => setError(reason instanceof Error ? reason.message : 'Unable to load payment-link options.'));
    }, []);

    const selectedPlan = useMemo(() => plans.find((plan) => plan.planId === planId), [planId, plans]);
    const loadUserData = async (resolvedUser: SubscriptionUserLookup) => {
        const [linkRows, evidenceRows] = await Promise.all([
            getPaymentLinks(resolvedUser.userId),
            getPaymentEvidence(resolvedUser.userId),
        ]);
        setLinks(linkRows);
        setEvidence(evidenceRows);
    };

    const lookup = async () => {
        if (!identifier.trim()) return;
        setLoading(true);
        setError('');
        setCreated(null);
        try {
            const resolved = await lookupSubscriptionUser(identifier.trim());
            setUser(resolved);
            await loadUserData(resolved);
        } catch (reason) {
            setUser(null);
            setLinks([]);
            setEvidence([]);
            setError(reason instanceof Error ? reason.message : 'User was not found.');
        } finally {
            setLoading(false);
        }
    };

    const create = async () => {
        if (!user || !planId || !countryCode) {
            setError('Select a user, plan and billing country.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const link = await createPaymentLink({ userId: user.userId, planId, countryCode, expiresInHours });
            setCreated(link);
            await loadUserData(user);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Unable to create the Airwallex payment link.');
        } finally {
            setLoading(false);
        }
    };

    const deactivate = async (link: PaymentLinkRecord) => {
        setLoading(true);
        setError('');
        try {
            await deactivatePaymentLink(link.paymentLinkId);
            if (user) await loadUserData(user);
            if (created?.paymentLinkId === link.paymentLinkId) setCreated(null);
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Unable to deactivate the payment link.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={800}>Create hosted payment link</Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    Creates a one-time, fixed-price checkout with Cashfree for India and Airwallex elsewhere. The QR code contains the same secure provider URL.
                </Typography>
                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }}>
                    <TextField label="User email or mobile" value={identifier}
                        onChange={(event) => setIdentifier(event.target.value)}
                        onKeyDown={(event) => { if (event.key === 'Enter') void lookup(); }}
                        fullWidth size="small" />
                    <Button variant="outlined" onClick={() => void lookup()} disabled={loading || !identifier.trim()}>
                        Find user
                    </Button>
                </Stack>
                {user ? <Alert severity={user.isActivated ? 'success' : 'warning'} sx={{ mt: 2 }}>
                    {`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'Unnamed user'} · {user.email} · {user.userId}
                </Alert> : null}
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} sx={{ mt: 2 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Plan</InputLabel>
                        <Select value={planId} label="Plan" onChange={(event) => setPlanId(String(event.target.value))}>
                            {plans.map((plan) => <MenuItem key={plan.planId} value={plan.planId}>{plan.name} ({plan.durationDays} days)</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Billing country</InputLabel>
                        <Select value={countryCode} label="Billing country" onChange={(event) => setCountryCode(String(event.target.value))}>
                            {countries.map((country) => <MenuItem key={country.countryCode} value={country.countryCode}>{country.countryName} ({country.countryCode})</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField label="Expiry hours" type="number" size="small" value={expiresInHours}
                        onChange={(event) => setExpiresInHours(Math.min(168, Math.max(1, Number(event.target.value))))}
                        slotProps={{ htmlInput: { min: 1, max: 168 } }} sx={{ minWidth: 140 }} />
                    <Button variant="contained" onClick={() => void create()} disabled={loading || !user || !selectedPlan || !countryCode}>
                        {loading ? <CircularProgress size={18} color="inherit" /> : 'Create link'}
                    </Button>
                </Stack>
            </Paper>

            {created ? <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} gap={3} alignItems="center">
                    <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1 }}>
                        <QRCodeSVG value={created.url} size={220} level="M" />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" fontWeight={800}>Ready to share</Typography>
                        <Typography variant="body2" color="text.secondary">{created.providerName}</Typography>
                        <Typography fontWeight={700}>{created.planName} · {created.currency} {created.amount}</Typography>
                        <Typography variant="body2" color="text.secondary">Expires {formatDateTime(created.expiresAtUtc)}</Typography>
                        <Typography sx={{ mt: 1, wordBreak: 'break-all' }}>{created.url}</Typography>
                        <Stack direction="row" gap={1} sx={{ mt: 2 }}>
                            <Button startIcon={<ContentCopyIcon />} variant="outlined" onClick={() => void navigator.clipboard.writeText(created.url)}>Copy</Button>
                            <Button startIcon={<LaunchIcon />} variant="contained" href={created.url} target="_blank" rel="noreferrer">Open secure checkout</Button>
                        </Stack>
                    </Box>
                </Stack>
            </Paper> : null}

            {user ? <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={800}>Payment links and evidence</Typography>
                {links.length === 0 ? <Typography color="text.secondary" sx={{ mt: 1 }}>No payment links for this user.</Typography> : links.map((link) => (
                    <Stack key={link.paymentLinkId} direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography fontWeight={700}>{link.planName} · {link.currency} {link.amount}</Typography>
                            <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{link.url}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">Created {formatDateTime(link.createdAtUtc)} · Expires {formatDateTime(link.expiresAtUtc)}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {link.providerName} · {link.source} · Email {link.emailStatus}
                                {link.emailAttemptCount ? ` (${link.emailAttemptCount} attempt${link.emailAttemptCount === 1 ? '' : 's'})` : ''}
                                {link.lastEmailErrorCode ? ` · ${link.lastEmailErrorCode}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Payment confirmation email {link.successEmailStatus}
                                {link.successEmailAttemptCount ? ` (${link.successEmailAttemptCount} attempt${link.successEmailAttemptCount === 1 ? '' : 's'})` : ''}
                                {link.lastSuccessEmailErrorCode ? ` · ${link.lastSuccessEmailErrorCode}` : ''}
                            </Typography>
                        </Box>
                        <Chip label={link.status} color={link.status === 'paid' ? 'success' : link.active ? 'warning' : 'default'} />
                        {link.active ? <Button color="error" startIcon={<LinkOffIcon />} onClick={() => void deactivate(link)}>Deactivate</Button> : null}
                    </Stack>
                ))}
                <Typography variant="subtitle1" fontWeight={800} sx={{ mt: 2 }}>Recent diagnostic evidence</Typography>
                {evidence.slice(0, 20).map((item) => <Box key={item.evidenceEventId} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" gap={1} alignItems="center">
                        <Chip size="small" label={item.outcome} color={item.outcome === 'failed' ? 'error' : item.outcome === 'succeeded' ? 'success' : 'default'} />
                        <Typography fontWeight={700}>{item.eventName}</Typography>
                        {item.errorCode ? <Typography color="error.main">{item.errorCode}</Typography> : null}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">{formatDateTime(item.occurredAtUtc)} · {item.source} · {item.paymentIntentId || 'no payment intent'}</Typography>
                    {item.safeDetails && item.safeDetails !== '{}' ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
                            {item.safeDetails}
                        </Typography>
                    ) : null}
                </Box>)}
            </Paper> : null}
        </Stack>
    );
}
